import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, Spinner, Switch } from '@material-tailwind/react';
import { BellIcon, GaugeIcon, SaveIcon, ShieldIcon, TimerIcon, AlertTriangleIcon } from 'lucide-react';
import {
    useGetSupportSettings,
    useUpdateSupportSettings,
} from '../../../custom-hooks/use-support';
import { useGetRoles } from '../../../custom-hooks/use-roles';
import { useAuth } from '../../../services/contexts/authContext';
import { STATIC_USER_ROLES, SUPPORT_TICKET_PRIORITIES_ENUM } from '../../../utils/enums';
import CategoriesSection from './CategoriesSection';

// Both SLA fields are stored in minutes (see services/supportSettings.js). The form
// edits first response in minutes and resolution in hours, since that's how the
// targets are actually spoken about, and converts on the way in and out.
const MINUTES_PER_HOUR = 60;

const formatMinutes = (minutes) => {
    if (minutes === '' || minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return 'not set';
    const value = Number(minutes);
    if (value < 60) return `${value} min`;
    if (value < 1440) return `${+(value / 60).toFixed(1)} hrs`;
    return `${+(value / 1440).toFixed(1)} days`;
};

const SettingsSupport = () => {
    const { userDetails } = useAuth();
    const { data: settingsData, isLoading } = useGetSupportSettings();
    const { data: rolesData, isLoading: isLoadingRoles } = useGetRoles();
    const updateSettingsMutation = useUpdateSupportSettings();

    const [rateLimitEnabled, setRateLimitEnabled] = useState(true);
    const [rateLimitWindowMs, setRateLimitWindowMs] = useState(600000); // 10 mins default
    const [rateLimitMax, setRateLimitMax] = useState(3);
    
    const [autoAssignEnabled, setAutoAssignEnabled] = useState(true);
    const [autoAssignRole, setAutoAssignRole] = useState(STATIC_USER_ROLES.SUPPORT_AGENT);

    const [statusChangeEnabled, setStatusChangeEnabled] = useState(false);
    const [statusChangeRoles, setStatusChangeRoles] = useState([]);

    const [slaEnabled, setSlaEnabled] = useState(true);
    const [slaTargets, setSlaTargets] = useState({});
    const [slaNotifyAssignedAgent, setSlaNotifyAssignedAgent] = useState(true);
    const [slaRoles, setSlaRoles] = useState([]);

    useEffect(() => {
        if (settingsData) {
            if (settingsData.rateLimit) {
                setRateLimitEnabled(settingsData.rateLimit.enabled ?? true);
                setRateLimitWindowMs(settingsData.rateLimit.windowMs || 600000);
                setRateLimitMax(settingsData.rateLimit.max || 3);
            }
            if (settingsData.autoAssign) {
                setAutoAssignEnabled(settingsData.autoAssign.enabled ?? true);
                setAutoAssignRole(settingsData.autoAssign.role || STATIC_USER_ROLES.SUPPORT_AGENT);
            }
            if (settingsData.statusChangeNotifications) {
                setStatusChangeEnabled(settingsData.statusChangeNotifications.enabled ?? false);
                setStatusChangeRoles(settingsData.statusChangeNotifications.roles || []);
            }
            if (settingsData.sla) {
                setSlaEnabled(settingsData.sla.enabled ?? true);
                setSlaTargets(settingsData.sla.targets || {});
                setSlaNotifyAssignedAgent(settingsData.sla.notifyAssignedAgent ?? true);
                setSlaRoles(settingsData.sla.roles || []);
            }
        }
    }, [settingsData]);

    const isSaving = updateSettingsMutation.isLoading;

    const handleSave = async () => {
        const payload = {
            settings: {
                rateLimit: {
                    enabled: rateLimitEnabled,
                    windowMs: Number(rateLimitWindowMs),
                    max: Number(rateLimitMax)
                },
                autoAssign: {
                    enabled: autoAssignEnabled,
                    role: autoAssignRole
                },
                statusChangeNotifications: {
                    enabled: statusChangeEnabled,
                    roles: statusChangeRoles
                },
                sla: {
                    enabled: slaEnabled,
                    targets: slaTargets,
                    notifyAssignedAgent: slaNotifyAssignedAgent,
                    roles: slaRoles
                }
            }
        };
        await updateSettingsMutation.mutateAsync(payload);
    };

    if (isLoading || isLoadingRoles) {
        return (
            <div className='min-h-[60vh] flex items-center justify-center'>
                <Spinner className='h-8 w-8' />
            </div>
        );
    }


    const hasAccess = userDetails?.role === STATIC_USER_ROLES.SUPER_ADMIN || 
                      userDetails?.role === STATIC_USER_ROLES.ADMIN ||
                      (userDetails?.role === STATIC_USER_ROLES.STAFF && userDetails?.dynamicRole?.permissions?.includes('FX22-4:manage'));

    if (!hasAccess) {
        return (
            <div className='flex min-h-[60vh] flex-col items-center justify-center p-8'>
                <div className='rounded-full bg-blue-gray-50 p-6'>
                    <AlertTriangleIcon size={48} className='text-blue-gray-300' />
                </div>
                <h2 className='mt-6 text-xl font-semibold text-blue-gray-800'>No Settings Available</h2>
                <p className='mt-2 text-center text-sm text-blue-gray-500'>
                    Your current role does not have access to this settings page.
                </p>
            </div>
        );
    }

    const roleOptions = rolesData ? rolesData.map(role => ({ id: role._id, name: role.name })) : [];

    const toggleRole = (role) => {
        if (statusChangeRoles.includes(role)) {
            setStatusChangeRoles(statusChangeRoles.filter(r => r !== role));
        } else {
            setStatusChangeRoles([...statusChangeRoles, role]);
        }
    };

    const toggleSlaRole = (role) => {
        if (slaRoles.includes(role)) {
            setSlaRoles(slaRoles.filter(r => r !== role));
        } else {
            setSlaRoles([...slaRoles, role]);
        }
    };

    const updateSlaTarget = (priority, field, minutes) => {
        setSlaTargets(prev => ({
            ...prev,
            [priority]: { ...(prev[priority] || {}), [field]: minutes }
        }));
    };

    return (
        <div className='p-4 md:p-8'>
            <div className='w-full space-y-6'>
                <div className='relative overflow-hidden rounded-2xl border border-blue-gray-100 bg-gradient-to-br from-zinc-50 via-white to-zinc-100 p-6'>
                    <div className='absolute -top-14 -right-8 h-40 w-40 rounded-full bg-zinc-200/50 blur-2xl' />
                    <div className='relative'>
                        <p className='text-xs uppercase tracking-[0.2em] text-blue-gray-500'>System Policy</p>
                        <h1 className='mt-1 text-2xl font-semibold text-blue-gray-900'>Support Settings</h1>
                        <p className='mt-2 text-sm text-blue-gray-600'>Manage rate limiting, auto-assignment, SLA targets and notifications for support tickets.</p>
                    </div>
                </div>

                <Card className='border border-blue-gray-100 shadow-sm'>
                    <CardBody className='space-y-8'>
                        
                        {/* Rate Limit Section */}
                        <div className='space-y-4'>
                            <div className='flex items-center justify-between border-b border-blue-gray-50 pb-2'>
                                <div className='flex items-center gap-2 text-blue-gray-800'>
                                    <ShieldIcon size={18} />
                                    <h3 className='font-semibold'>Ticket Creation Rate Limit</h3>
                                </div>
                                <div className='flex items-center gap-2'>
                                    <span className='text-xs font-semibold uppercase tracking-wider text-blue-gray-500'>
                                        {rateLimitEnabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    <Switch
                                        id="rate-limit-switch"
                                        color="blue"
                                        checked={rateLimitEnabled}
                                        onChange={(e) => setRateLimitEnabled(e.target.checked)}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>

                            {rateLimitEnabled && (
                                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                                    <div className='rounded-xl border border-blue-gray-100 p-4'>
                                        <div className='mb-3 flex items-center gap-2 text-blue-gray-700'>
                                            <GaugeIcon size={16} />
                                            <p className='text-sm font-semibold'>Time Window (Minutes)</p>
                                        </div>
                                        <input
                                            type='number'
                                            min={1}
                                            value={rateLimitWindowMs / 60000} // Convert ms to mins for display
                                            onChange={(e) => setRateLimitWindowMs(Number(e.target.value) * 60000)}
                                            className='w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-gray-700 focus:outline-none focus:ring-0'
                                            disabled={isSaving}
                                        />
                                        <p className='mt-2 text-xs text-blue-gray-500'>The rolling window in which tickets are counted.</p>
                                    </div>
                                    <div className='rounded-xl border border-blue-gray-100 p-4'>
                                        <div className='mb-3 flex items-center gap-2 text-blue-gray-700'>
                                            <GaugeIcon size={16} />
                                            <p className='text-sm font-semibold'>Max Tickets</p>
                                        </div>
                                        <input
                                            type='number'
                                            min={1}
                                            value={rateLimitMax}
                                            onChange={(e) => setRateLimitMax(e.target.value)}
                                            className='w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-gray-700 focus:outline-none focus:ring-0'
                                            disabled={isSaving}
                                        />
                                        <p className='mt-2 text-xs text-blue-gray-500'>Maximum number of tickets a customer can create within the window.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Auto Assign Section */}
                        <div className='space-y-4'>
                            <div className='flex items-center justify-between border-b border-blue-gray-50 pb-2'>
                                <div className='flex items-center gap-2 text-blue-gray-800'>
                                    <ShieldIcon size={18} />
                                    <h3 className='font-semibold'>Auto-Assignment</h3>
                                </div>
                                <div className='flex items-center gap-2'>
                                    <span className='text-xs font-semibold uppercase tracking-wider text-blue-gray-500'>
                                        {autoAssignEnabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    <Switch
                                        id="auto-assign-switch"
                                        color="blue"
                                        checked={autoAssignEnabled}
                                        onChange={(e) => setAutoAssignEnabled(e.target.checked)}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>

                            {autoAssignEnabled && (
                                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                                    <div className='rounded-xl border border-blue-gray-100 p-4'>
                                        <div className='mb-3 flex items-center gap-2 text-blue-gray-700'>
                                            <p className='text-sm font-semibold'>Target Role</p>
                                        </div>
                                        <select
                                            value={autoAssignRole}
                                            onChange={(e) => setAutoAssignRole(e.target.value)}
                                            className='w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-gray-700 focus:outline-none focus:ring-0'
                                            disabled={isSaving}
                                        >
                                            {roleOptions.map((role) => (
                                                <option key={role.id} value={role.id}>{role.name}</option>
                                            ))}
                                        </select>
                                        <p className='mt-2 text-xs text-blue-gray-500'>The user role that incoming tickets will automatically be assigned to.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Status Change Notifications Section */}
                        <div className='space-y-4'>
                            <div className='flex items-center justify-between border-b border-blue-gray-50 pb-2'>
                                <div className='flex items-center gap-2 text-blue-gray-800'>
                                    <ShieldIcon size={18} />
                                    <h3 className='font-semibold'>Status Change Notifications</h3>
                                </div>
                                <div className='flex items-center gap-2'>
                                    <span className='text-xs font-semibold uppercase tracking-wider text-blue-gray-500'>
                                        {statusChangeEnabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    <Switch
                                        id="status-change-switch"
                                        color="blue"
                                        checked={statusChangeEnabled}
                                        onChange={(e) => setStatusChangeEnabled(e.target.checked)}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>

                            {statusChangeEnabled && (
                                <div className='grid grid-cols-1 gap-4'>
                                    <div className='rounded-xl border border-blue-gray-100 p-4'>
                                        <div className='mb-3 flex items-center gap-2 text-blue-gray-700'>
                                            <p className='text-sm font-semibold'>Roles to Notify</p>
                                        </div>
                                        <div className='flex flex-wrap gap-2'>
                                            {roleOptions.map((role) => {
                                                const isSelected = statusChangeRoles.includes(role.id);
                                                return (
                                                    <button
                                                        key={role.id}
                                                        onClick={() => toggleRole(role.id)}
                                                        disabled={isSaving}
                                                        className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${isSelected ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-blue-gray-600 border-blue-gray-200 hover:border-blue-400'}`}
                                                    >
                                                        {role.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <p className='mt-3 text-xs text-blue-gray-500'>Users with these roles will receive an in-app notification whenever any ticket's status is updated.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <CategoriesSection />

                        {/* SLA Targets Section */}
                        <div className='space-y-4'>
                            <div className='flex items-center justify-between border-b border-blue-gray-50 pb-2'>
                                <div className='flex items-center gap-2 text-blue-gray-800'>
                                    <TimerIcon size={18} />
                                    <h3 className='font-semibold'>SLA Targets</h3>
                                </div>
                                <div className='flex items-center gap-2'>
                                    <span className='text-xs font-semibold uppercase tracking-wider text-blue-gray-500'>
                                        {slaEnabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    <Switch
                                        id="sla-switch"
                                        color="blue"
                                        checked={slaEnabled}
                                        onChange={(e) => setSlaEnabled(e.target.checked)}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>

                            {slaEnabled && (
                                <div className='space-y-3'>
                                    {/* Rows are generated from the priority enum, so adding a
                                        new priority level automatically gains a row here. */}
                                    {SUPPORT_TICKET_PRIORITIES_ENUM.map((priority) => {
                                        const target = slaTargets[priority] || {};
                                        const resolveHours = target.resolveMinutes === undefined || target.resolveMinutes === ''
                                            ? ''
                                            : Number(target.resolveMinutes) / MINUTES_PER_HOUR;
                                        return (
                                            <div key={priority} className='rounded-xl border border-blue-gray-100 p-4'>
                                                <p className='mb-3 text-sm font-semibold capitalize text-blue-gray-700'>{priority}</p>
                                                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                                                    <div>
                                                        <label className='mb-1.5 block text-xs font-medium text-blue-gray-600'>
                                                            First response (minutes)
                                                        </label>
                                                        <input
                                                            type='number'
                                                            min={1}
                                                            value={target.firstResponseMinutes ?? ''}
                                                            onChange={(e) => updateSlaTarget(
                                                                priority,
                                                                'firstResponseMinutes',
                                                                e.target.value === '' ? '' : Number(e.target.value)
                                                            )}
                                                            className='w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-gray-700 focus:outline-none focus:ring-0'
                                                            disabled={isSaving}
                                                        />
                                                        <p className='mt-1.5 text-xs text-blue-gray-500'>
                                                            = {formatMinutes(target.firstResponseMinutes)}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <label className='mb-1.5 block text-xs font-medium text-blue-gray-600'>
                                                            Resolve within (hours)
                                                        </label>
                                                        <input
                                                            type='number'
                                                            min={1}
                                                            value={resolveHours}
                                                            onChange={(e) => updateSlaTarget(
                                                                priority,
                                                                'resolveMinutes',
                                                                e.target.value === '' ? '' : Number(e.target.value) * MINUTES_PER_HOUR
                                                            )}
                                                            className='w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-gray-700 focus:outline-none focus:ring-0'
                                                            disabled={isSaving}
                                                        />
                                                        <p className='mt-1.5 text-xs text-blue-gray-500'>
                                                            = {formatMinutes(target.resolveMinutes)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <p className='text-xs text-blue-gray-500'>
                                        Tickets are checked every 15 minutes. A ticket breaches when it passes its
                                        first-response target with no staff reply, or passes its resolution target while still open.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* SLA Breach Notifications Section */}
                        <div className='space-y-4'>
                            <div className='flex items-center gap-2 border-b border-blue-gray-50 pb-2 text-blue-gray-800'>
                                <BellIcon size={18} />
                                <h3 className='font-semibold'>SLA Breach Notifications</h3>
                            </div>

                            <div className='rounded-xl border border-blue-gray-100 p-4'>
                                <div className='mb-4 flex items-center justify-between'>
                                    <div>
                                        <p className='text-sm font-semibold text-blue-gray-700'>Notify the assigned agent</p>
                                        <p className='mt-1 text-xs text-blue-gray-500'>
                                            The person the ticket is assigned to is alerted when it breaches.
                                        </p>
                                    </div>
                                    <Switch
                                        id="sla-notify-agent-switch"
                                        color="blue"
                                        checked={slaNotifyAssignedAgent}
                                        onChange={(e) => setSlaNotifyAssignedAgent(e.target.checked)}
                                        disabled={isSaving}
                                    />
                                </div>

                                <p className='mb-3 text-sm font-semibold text-blue-gray-700'>Roles to Notify</p>
                                <div className='flex flex-wrap gap-2'>
                                    {roleOptions.map((role) => {
                                        const isSelected = slaRoles.includes(role.id);
                                        return (
                                            <button
                                                key={role.id}
                                                onClick={() => toggleSlaRole(role.id)}
                                                disabled={isSaving}
                                                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${isSelected ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-blue-gray-600 border-blue-gray-200 hover:border-blue-400'}`}
                                            >
                                                {role.name}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className='mt-3 text-xs text-blue-gray-500'>
                                    {slaRoles.length === 0
                                        ? 'No roles selected, so breach alerts go to everyone with support access. Nothing will be missed.'
                                        : 'Users with these roles are alerted whenever a ticket breaches its SLA.'}
                                </p>
                            </div>
                        </div>

                        <div className='flex flex-col gap-3 md:flex-row pt-4'>
                            <Button
                                className='min-w-[160px] rounded-full flex items-center justify-center gap-2 bg-black text-white shadow-none transition-all duration-200 hover:bg-zinc-800 hover:-translate-y-[1px] hover:shadow-md active:translate-y-0 active:bg-zinc-900 focus:outline-none focus:ring-0 disabled:opacity-60'
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? <Spinner className='h-4 w-4' /> : <SaveIcon size={16} />}
                                Save Settings
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default SettingsSupport;
